import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { ChatUISchema } from '../../Chat/types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChatContainer } from '../../Chat/ChatContainer'
import { useChat } from '../../Chat/hooks'
import { MessageList } from '../../Chat/MessageList'
import { ChatInput } from '../../Chat/ChatInput'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'

export function ChatContent({ uiSchema, tileFilters }: { uiSchema: ChatUISchema; tileFilters?: React.ReactNode }) {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(() => scrollAreaRef.current?.clientHeight)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const { title, description, samplePrompts, emphasizeHeader } = uiSchema
  const { messages, isLoading, error } = useChat()
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  useLayoutEffect(() => {
    setHeight(scrollAreaRef.current?.clientHeight)
  }, [])

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight
      }
    }
  }

  // Function to check if scrolled to bottom
  const checkScrollPosition = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollElement) {
        const isAtBottom = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10
        setShowScrollButton(!isAtBottom)
      }
    }
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading])

  // Set up scroll event listener
  useEffect(() => {
    const scrollElement = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScrollPosition)
      // Initial check
      checkScrollPosition()

      return () => {
        scrollElement.removeEventListener('scroll', checkScrollPosition)
      }
    }
  }, [scrollAreaRef])

  return (
    <div className="relative flex h-full flex-col">
      {title && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <ScrollArea style={{ height: contentHeight }} ref={scrollAreaRef}>
        <div style={{ minHeight: height }} className="mx-auto flex max-w-5xl flex-col items-center justify-center">
          <ChatContainer>
            {/* Messages */}
            <MessageList />

            {/* Floating scroll to bottom button */}
            {showScrollButton && (
              <Button
                onClick={scrollToBottom}
                variant="outline"
                size="icon"
                className="absolute bottom-[15%] right-1/2 z-10 flex h-10 w-10 translate-x-1/2 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105"
                aria-label="Scroll to bottom"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            )}

            {/* Error display */}
            {error && (
              <div className="border-b border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">Failed to send message. Please try again.</p>
              </div>
            )}

            {/* Input */}
            <ChatInput samplePrompts={samplePrompts} className="bg-background/80 pt-0 backdrop-blur" />
          </ChatContainer>
        </div>
      </ScrollArea>
    </div>
  )
}
